import React, { useState } from 'react';
import { CatchRecord, computeBadgeStates } from '../../utils/memberStats';

interface AchievementsBadgesProps {
  catches: CatchRecord[];
  placements: Array<{ species: string; position: number }>;
}

const BADGES = [
  {
    key: 'firstCatch' as const,
    emoji: '🎣',
    title: 'First Catch',
    description: 'Submit your first approved catch in a competition.',
  },
  {
    key: 'tenFishClub' as const,
    emoji: '🐟',
    title: '10 Fish Club',
    description: 'Get 10 approved catches across all competitions.',
  },
  {
    key: 'allRounder' as const,
    emoji: '🎯',
    title: 'All-Rounder',
    description: 'Catch all seven species — Flathead, Bream, Jewfish, Mangrove Jack, Whiting, Tailor, and Trevally.',
  },
  {
    key: 'catchAndReleaseHero' as const,
    emoji: '♻️',
    title: 'Catch & Release Hero',
    description: 'Release 80% or more of your catches. Champion of the river!',
  },
  {
    key: 'competitionRegular' as const,
    emoji: '📅',
    title: 'Competition Regular',
    description: 'Enter 5 or more competitions. A true club regular.',
  },
  {
    key: 'goldPlace' as const,
    emoji: '🥇',
    title: '1st Place',
    description: 'Finish 1st in any species category in a published competition.',
  },
  {
    key: 'silverPlace' as const,
    emoji: '🥈',
    title: '2nd Place',
    description: 'Finish 2nd in any species category in a published competition.',
  },
  {
    key: 'bronzePlace' as const,
    emoji: '🥉',
    title: '3rd Place',
    description: 'Finish 3rd in any species category in a published competition.',
  },
  {
    key: 'seasonChampion' as const,
    emoji: '🏆',
    title: 'Season Champion',
    description: 'Finish in the top 3 in any species category across the season.',
  },
  {
    key: 'bigFish' as const,
    emoji: '🐋',
    title: 'Big Fish',
    description: 'Catch a fish measuring 50cm or more in a competition.',
  },
  {
    key: 'hatTrick' as const,
    emoji: '🎩',
    title: 'Hat Trick',
    description: 'Finish 1st in 3 or more different species categories across your career.',
  },
  {
    key: 'centuryClub' as const,
    emoji: '💯',
    title: 'Century Club',
    description: 'Get 100 approved catches across all competitions. A true fishing legend!',
  },
];

const AchievementsBadges: React.FC<AchievementsBadgesProps> = ({ catches, placements }) => {
  const states = computeBadgeStates(catches, placements);
  const [expanded, setExpanded] = useState<string | null>(null);

  const earned = BADGES.filter((b) => states[b.key]);
  const unearned = BADGES.filter((b) => !states[b.key]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Achievement Badges
        {earned.length > 0 && (
          <span className="ml-2 text-sm font-normal text-gray-500">
            {earned.length} of {BADGES.length} earned
          </span>
        )}
      </h3>

      {/* Earned badges */}
      {earned.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {earned.map((badge) => (
              <button
                key={badge.key}
                onClick={() => setExpanded(expanded === badge.key ? null : badge.key)}
                className="flex flex-col items-center text-center p-3 rounded-lg bg-blue-50 border border-blue-100 hover:bg-blue-100 transition-colors"
              >
                <span className="text-3xl mb-1">{badge.emoji}</span>
                <span className="text-xs font-semibold text-gray-900 leading-tight">{badge.title}</span>
                <span className="text-xs text-green-600 font-medium mt-0.5">✓ Earned</span>
                {expanded === badge.key && (
                  <p className="text-xs text-gray-600 mt-2 text-left leading-snug">{badge.description}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Unearned badges */}
      {unearned.length > 0 && (
        <div>
          {earned.length > 0 && (
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Still to unlock</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {unearned.map((badge) => (
              <button
                key={badge.key}
                onClick={() => setExpanded(expanded === badge.key ? null : badge.key)}
                className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors opacity-60"
              >
                <span className="text-3xl mb-1 grayscale">{badge.emoji}</span>
                <span className="text-xs font-medium text-gray-600 leading-tight">{badge.title}</span>
                {expanded === badge.key && (
                  <p className="text-xs text-gray-500 mt-2 text-left leading-snug">{badge.description}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {earned.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          No badges yet — get out there and start fishing! 🎣
        </p>
      )}

      <p className="text-xs text-gray-400 mt-4 text-center">Tap a badge to see how to earn it</p>
    </div>
  );
};

export default AchievementsBadges;
